import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutAlbumVideoResult,
  PutAlbumVideoOptions,
  DeleteAlbumVideoResult,
  DeleteAlbumVideoOptions,
  GetAlbumVideoOptions,
  GetAlbumVideoResult,
  getAlbumVideo,
  PutAlbumVideoFields,
  DeleteAlbumVideoFields,
  GetAlbumVideoFields,
  putAlbumVideo,
  deleteAlbumVideo,
} from '../fetchers/album/video';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetAlbumVideoOptions<Fields extends GetAlbumVideoFields> = GetAlbumVideoOptions<Fields>;

/**
 * Get a specific video in a showcase
 * This method returns a single video belonging to the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbumVideo<Fields extends GetAlbumVideoFields>(
  getOptions: UseGetAlbumVideoOptions<Fields> | (() => UseGetAlbumVideoOptions<Fields> | null),
  config?: SWRConfiguration<GetAlbumVideoResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/albums/${options.where.albumId}/videos/${options.where.videoId}${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getAlbumVideo({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideo, {
    endpoint: '/albums/:albumId/videos/:videoId',
    method: 'GET',
  });
}

/**
 * Get a specific video in a showcase
 * This method returns a single video belonging to the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function useGetAlbumVideoLazy<Fields extends GetAlbumVideoFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetAlbumVideoResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetAlbumVideoOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${options.where.videoId}${serializeQuery(
            options,
          )}`,
          getAlbumVideo({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetAlbumVideoLazy, {
    endpoint: '/albums/:albumId/videos/:videoId',
    method: 'GET',
  });
}

type UsePutAlbumVideoOptions<Fields extends PutAlbumVideoFields> = PutAlbumVideoOptions<Fields>;

/**
 * Add a specific video to a showcase
 * This method adds a single video to the specified showcase. The authenticated user must be the owner of the showcase.
 */

export function usePutAlbumVideo<Fields extends PutAlbumVideoFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutAlbumVideoResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutAlbumVideoOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${options.where.videoId}${serializeQuery(
            options,
          )}`,
          putAlbumVideo({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutAlbumVideo, {
    endpoint: '/albums/:albumId/videos/:videoId',
    method: 'PUT',
  });
}

type UseDeleteAlbumVideoOptions<Fields extends DeleteAlbumVideoFields> =
  DeleteAlbumVideoOptions<Fields>;

/**
 * Remove a video from a showcase
 * This method removes the specified video from its showcase. The authenticated user must be the owner of the showcase.
 */

export function useDeleteAlbumVideo<Fields extends DeleteAlbumVideoFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteAlbumVideoResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteAlbumVideoOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/albums/${options.where.albumId}/videos/${options.where.videoId}${serializeQuery(
            options,
          )}`,
          deleteAlbumVideo({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteAlbumVideo, {
    endpoint: '/albums/:albumId/videos/:videoId',
    method: 'DELETE',
  });
}
